/**
 * CarouselsConfig — TZ 2026-09-23 Блок 5: именованные карусели ключей.
 *
 * Карусель = набор ключей одного провайдера; состав и тарифы настраиваются
 * здесь (backend /admin/carousels CRUD). Ключи вне карусели в её ротацию
 * не попадают. Search-модули выберут карусель как источник ссылок
 * ("carousel:<id>"), LLM-провайдер — через params.llm_provider.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  useCarousels,
  useCreateCarousel,
  useDeleteCarousel,
  useProviders,
  useUpdateCarousel,
  type AIProviderConfig,
  type KeyCarouselSet,
  type ProviderName,
} from '@/hooks/useAdmin';
import { Modal } from '@/components/ui/Modal';
import { providerBadge, providerLabel, TIER_CHIP_CLASSES } from '@/components/admin/providerMeta';
import { cn } from '@/lib/utils';

const TIER_KEYS = ['economy', 'optimal', 'max', 'super'] as const;

function uniqueProviderNames(list: AIProviderConfig[]): ProviderName[] {
  return Array.from(new Set(list.map((k) => k.provider)));
}
type TierKey = (typeof TIER_KEYS)[number];

interface FormState {
  name: string;
  provider: ProviderName;
  is_active: boolean;
  allowed_tiers: TierKey[];
  key_ids: string[];
}

const emptyForm = (): FormState => ({
  name: '',
  provider: 'openai',
  is_active: true,
  allowed_tiers: ['economy', 'optimal', 'max', 'super'],
  key_ids: [],
});

function TierChips({ tiers, className }: { tiers: string[]; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {TIER_KEYS.map((tk) => {
        const on = (tiers ?? []).includes(tk);
        const cls = on ? TIER_CHIP_CLASSES[tk]?.on : 'border-surface-700 text-surface-500';
        return (
          <span key={tk} className={'text-[10px] px-1 py-0.5 rounded border ' + cls}>
            {t('system.tiers.' + tk)}
          </span>
        );
      })}
    </span>
  );
}

export function CarouselsConfig() {
  const { t } = useTranslation();
  const { data: carousels, isLoading } = useCarousels();
  const { data: providers } = useProviders();
  const del = useDeleteCarousel();
  const [editTarget, setEditTarget] = useState<KeyCarouselSet | 'new' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const openNew = () => {
    setActionError(null);
    setEditTarget('new');
  };
  const openEdit = (c: KeyCarouselSet) => {
    setActionError(null);
    setEditTarget(c);
  };

  const handleDelete = async (c: KeyCarouselSet) => {
    setActionError(null);
    try {
      await del.mutateAsync(c.id);
    } catch (e) {
      const detail = extractDetail(e);
      setActionError(detail ?? t('system.carousels.deleteFailed', { defaultValue: 'Не удалось удалить карусель.' }));
    }
  };

  /* submit lives in CarouselModal below */

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-md font-semibold">{t('system.carousels.title')}</h3>
        <button type="button" className="btn-primary w-fit" onClick={openNew}>
          <Plus className="w-4 h-4" />
          {t('system.carousels.create')}
        </button>
      </div>
      {actionError && <div className="text-xs text-danger">{actionError}</div>}
      {isLoading && (
        <div className="text-sm text-surface-400">{t('common.loading', { defaultValue: 'Загрузка…' })}</div>
      )}
      {!isLoading && (carousels ?? []).length === 0 && (
        <div className="card p-4 border border-dashed border-surface-700 text-center text-sm text-surface-500">
          {t('system.carousels.empty')}
        </div>
      )}
      <div className="space-y-2">
        {(carousels ?? []).map((c) => (
          <div key={c.id} className="card p-3 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <span className={'text-xs px-2 py-0.5 rounded-md border font-semibold ' + providerBadge(c.provider)}>
                  {providerLabel(c.provider)}
                </span>
                <span className="font-semibold truncate">{c.name}</span>
                {!c.is_active && (
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-surface-700/40 text-surface-400">
                    {t('system.carousels.inactive')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-primary-500/10 text-surface-400 hover:text-primary-400"
                  onClick={() => openEdit(c)}
                  title={t('system.carousels.edit')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-danger-tint/10 text-surface-400 hover:text-danger"
                  onClick={() => void handleDelete(c)}

                  title={t('system.carousels.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <TierChips tiers={c.allowed_tiers ?? []} />
            <div className="flex flex-wrap gap-1">
              {(c.keys ?? []).map((k) => (
                <span
                  key={k.id}
                  className="text-[10px] px-1.5 py-0.5 rounded-md bg-surface-800 text-surface-300 font-mono"
                  title={k.api_key_masked ?? ''}
                >
                  {k.display_name}
                </span>
              ))}
              {(c.keys ?? []).length === 0 && (
                <span className="text-xs text-surface-500">{t('system.carousels.noKeys')}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <CarouselModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
      />
    </div>
  );
}

function extractDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d.trim() !== '') return d;
  return null;
}

function CarouselModal(props: {
  target: KeyCarouselSet | 'new' | null;
  onClose: () => void;
}) {
  const { target, onClose } = props;
  const { t } = useTranslation();
  const { data: providers } = useProviders();
  const create = useCreateCarousel();
  const update = useUpdateCarousel();
  const isNew = target === 'new';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
    if (target && target !== 'new') {
      setForm({
        name: target.name,
        provider: target.provider,
        is_active: target.is_active,
        allowed_tiers: ((target.allowed_tiers?.length ? target.allowed_tiers : [...TIER_KEYS]) as TierKey[]),
        key_ids: target.key_ids ?? [],
      });
    } else {
      setForm(emptyForm());
    }
  }, [target]);

  if (!target) return null;
  const available = (providers ?? []).filter((k) => k.provider === form.provider);
  const pending = create.isPending || update.isPending;

  const providerChoices = uniqueProviderNames(providers ?? []);
  const submit = async () => {
    setErr(null);
    const body = {
      name: form.name.trim(),
      provider: form.provider,
      is_active: form.is_active,
      allowed_tiers: form.allowed_tiers,
      key_ids: form.key_ids,
    };
    try {
      if (target === 'new') {
        await create.mutateAsync(body);
      } else if (target) {
        await update.mutateAsync({ id: target.id, body });
      }
      onClose();
    } catch (e) {
      setErr(extractDetail(e) ?? t('system.carousels.saveFailed'));
    }
  };

  const toggleKey = (id: string) => {
    setForm((f) => ({
      ...f,
      key_ids: f.key_ids.includes(id) ? f.key_ids.filter((x) => x !== id) : [...f.key_ids, id],
    }));
  };

  const toggleTier = (tk: TierKey) => {
    setForm((f) => ({
      ...f,
      allowed_tiers: f.allowed_tiers.includes(tk)
        ? f.allowed_tiers.filter((x) => x !== tk)
        : [...f.allowed_tiers, tk],
    }));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? t('system.carousels.create') : t('system.carousels.edit')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={pending}>
            {t('system.providers.cancel')}
          </button>
          <button type="button" className="btn-primary" disabled={pending || form.name.trim() === ''} onClick={() => void submit()}>
            {pending ? t('system.providers.saving') : t('system.providers.save')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {err && <div className="text-xs text-danger">{err}</div>}
        <div>
          <label className="text-xs text-surface-400 mb-1 block">{t('system.carousels.name')}</label>
          <input
            className="input w-full"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">{t('system.carousels.provider')}</label>
          <select
            className="input w-full"
            value={form.provider}
            disabled={!isNew}
            onChange={(e) => {
              setForm((f) => ({ ...f, provider: e.target.value as ProviderName, key_ids: [] }));
            }}
          >
            {providerChoices.map((p) => (
              <option key={p} value={p}>{providerLabel(p)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">{t('system.providers.tiers.label')}</label>
          <div className="flex flex-wrap gap-2">
            {TIER_KEYS.map((tk) => {
              const on = form.allowed_tiers.includes(tk);
              const cls = on ? TIER_CHIP_CLASSES[tk].on : 'border-surface-700 text-surface-500';
              return (
                <button
                  key={tk}
                  type="button"
                  className={'text-xs px-2 py-1 rounded-md border transition-colors ' + cls}
                  onClick={() => toggleTier(tk)}
                >
                  {t('system.tiers.' + tk)}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">{t('system.carousels.keys')}</label>
          <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-surface-700 p-2">
            {available.map((k) => (
              <label key={k.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.key_ids.includes(k.id)}
                  onChange={() => toggleKey(k.id)}
                />
                <span className="truncate">{k.display_name}</span>
                <span className="text-xs text-surface-500 font-mono">{k.api_key_masked}</span>
              </label>
            ))}
            {available.length === 0 && (
              <div className="text-xs text-surface-500">{t('system.providers.no_keys', { defaultValue: 'Нет ключей этого провайдера' })}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {form.key_ids.filter((id) => !available.some((k) => k.id === id)).map((id) => (
              <button
                key={id}
                type="button"
                className="text-[10px] px-1.5 py-0.5 rounded-md bg-surface-800 text-surface-300 font-mono hover:text-danger"
                onClick={() => toggleKey(id)}
              >
                {t('system.providers.display_name', { defaultValue: 'имя' })} {id.slice(0, 8)}…
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          {t('system.carousels.active')}
        </label>
      </div>
    </Modal>
  );
}
